import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../components/ui/select';
import { 
  Eye, MousePointerClick, Heart, MessageSquare, TrendingUp, TrendingDown,
  BarChart3, Download, Clock, Award, Loader2, ArrowLeft, ExternalLink, Sparkles
} from 'lucide-react';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, 
  Tooltip, ResponsiveContainer, Legend, AreaChart, Area
} from 'recharts';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';
import AIInsightsCard from '../components/AIInsightsCard';
import { useCountry } from '../context/CountryContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const UserAnalytics = () => {
  const { formatCurrency } = useCountry();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [period, setPeriod] = useState('month');
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [viewsTimeline, setViewsTimeline] = useState([]);
  const [hourlyActivity, setHourlyActivity] = useState(null);
  const [topAds, setTopAds] = useState([]);
  const [exporting, setExporting] = useState(false);
  
  const token = localStorage.getItem('token');
  
  useEffect(() => {
    if (!token) {
      navigate('/login', { state: { from: '/analytics' } });
      return;
    }
    fetchAllData();
  }, [token, period]);
  
  const fetchAllData = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const [dashboardRes, viewsRes, hourlyRes, topAdsRes] = await Promise.all([
        axios.get(`${API_URL}/api/analytics/dashboard?period=${period}`, { headers }),
        axios.get(`${API_URL}/api/analytics/views-over-time?period=${period}`, { headers }),
        axios.get(`${API_URL}/api/analytics/hourly-activity?period=${period}`, { headers }),
        axios.get(`${API_URL}/api/analytics/top-ads?period=${period}&limit=5`, { headers })
      ]);
      
      setDashboard(dashboardRes.data);
      setViewsTimeline(viewsRes.data);
      setHourlyActivity(hourlyRes.data);
      setTopAds(topAdsRes.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast({
        title: 'Error',
        description: 'Failed to load analytics data',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };
  
  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await axios.get(
        `${API_URL}/api/analytics/export?period=${period}`,
        { 
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `zeilian_analytics_${period}_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast({
        title: 'Export Complete',
        description: 'Analytics data has been downloaded'
      });
    } catch (error) {
      console.error('Error exporting:', error);
      toast({
        title: 'Export Failed',
        description: 'Could not export analytics data',
        variant: 'destructive'
      });
    } finally {
      setExporting(false);
    }
  };
  
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };
  
  const formatHour = (hour) => {
    if (hour === 0) return '12 AM';
    if (hour === 12) return '12 PM';
    return hour < 12 ? `${hour} AM` : `${hour - 12} PM`;
  };
  
  const MetricCard = ({ title, value, change, icon: Icon, color }) => (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500 mb-1">{title}</p>
            <p className="text-3xl font-bold">{value?.toLocaleString() || 0}</p>
            {change !== undefined && change !== 0 && (
              <div className={`flex items-center mt-2 text-sm ${change > 0 ? 'text-green-600' : 'text-red-600'}`}>
                {change > 0 ? <TrendingUp className="h-4 w-4 mr-1" /> : <TrendingDown className="h-4 w-4 mr-1" />}
                {Math.abs(change)}% vs last period
              </div>
            )}
          </div>
          <div className={`p-3 rounded-full ${color}`}>
            <Icon className="h-6 w-6 text-white" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
  
  if (!token) return null;
  
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-8">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center space-x-3 mb-2">
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-white hover:bg-white/20"
                  onClick={() => navigate('/profile')}
                >
                  <ArrowLeft className="h-5 w-5" />
                </Button>
                <h1 className="text-3xl font-bold">Analytics Dashboard</h1>
              </div>
              <p className="text-blue-100 ml-11">Track your ad performance and insights</p>
            </div>
            <div className="flex items-center space-x-3">
              <Select value={period} onValueChange={setPeriod}>
                <SelectTrigger className="w-[150px] bg-white/10 border-white/20 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="week">Last 7 Days</SelectItem>
                  <SelectItem value="month">Last 30 Days</SelectItem>
                  <SelectItem value="year">Last Year</SelectItem>
                  <SelectItem value="all">All Time</SelectItem>
                </SelectContent>
              </Select>
              <Button 
                variant="outline" 
                className="bg-white/10 border-white/20 text-white hover:bg-white/20"
                onClick={handleExport}
                disabled={exporting}
              >
                {exporting ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Download className="h-4 w-4 mr-2" />
                )}
                Export CSV
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          </div>
        ) : (
          <>
            {/* Overview Stats */}
            <div className="mb-8">
              <h2 className="text-xl font-semibold mb-4">Overview</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
                  <CardContent className="p-4">
                    <p className="text-blue-100 text-sm">Total Ads</p>
                    <p className="text-3xl font-bold">{dashboard?.overview?.total_ads || 0}</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
                  <CardContent className="p-4">
                    <p className="text-green-100 text-sm">Active</p>
                    <p className="text-3xl font-bold">{dashboard?.overview?.active_ads || 0}</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-yellow-500 to-yellow-600 text-white">
                  <CardContent className="p-4">
                    <p className="text-yellow-100 text-sm">Pending</p>
                    <p className="text-3xl font-bold">{dashboard?.overview?.pending_ads || 0}</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-gray-500 to-gray-600 text-white">
                  <CardContent className="p-4">
                    <p className="text-gray-100 text-sm">Expired</p>
                    <p className="text-3xl font-bold">{dashboard?.overview?.expired_ads || 0}</p>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Key Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
              <MetricCard
                title="Total Views"
                value={dashboard?.metrics?.views?.value}
                change={dashboard?.metrics?.views?.change}
                icon={Eye}
                color="bg-blue-500"
              />
              <MetricCard
                title="Unique Visitors"
                value={dashboard?.metrics?.unique_visitors?.value}
                change={dashboard?.metrics?.unique_visitors?.change}
                icon={BarChart3}
                color="bg-purple-500"
              />
              <MetricCard
                title="Contact Clicks"
                value={dashboard?.metrics?.contacts?.value}
                change={dashboard?.metrics?.contacts?.change}
                icon={MousePointerClick}
                color="bg-green-500"
              />
              <MetricCard
                title="Favorites"
                value={dashboard?.metrics?.favorites?.value}
                change={dashboard?.metrics?.favorites?.change}
                icon={Heart}
                color="bg-red-500"
              />
              <MetricCard
                title="Messages"
                value={dashboard?.metrics?.messages?.value}
                change={dashboard?.metrics?.messages?.change}
                icon={MessageSquare}
                color="bg-orange-500"
              />
            </div>

            {/* AI Insights Section */}
            <div className="mb-8">
              <AIInsightsCard variant="full" showRefresh={true} />
            </div>

            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              {/* Views Over Time */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <TrendingUp className="h-5 w-5 mr-2 text-blue-600" />
                    Views Over Time
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={viewsTimeline}>
                        <defs>
                          <linearGradient id="viewsGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                            <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis 
                          dataKey="date" 
                          tickFormatter={formatDate}
                          tick={{ fontSize: 12 }}
                          stroke="#9ca3af"
                        />
                        <YAxis tick={{ fontSize: 12 }} stroke="#9ca3af" />
                        <Tooltip 
                          labelFormatter={formatDate}
                          contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb' }}
                        />
                        <Area
                          type="monotone"
                          dataKey="views"
                          stroke="#3b82f6"
                          strokeWidth={2}
                          fill="url(#viewsGradient)"
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              {/* Hourly Activity */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Clock className="h-5 w-5 mr-2 text-purple-600" />
                    Best Time to Post
                  </CardTitle>
                  <CardDescription>
                    {hourlyActivity?.insights?.recommendation}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={hourlyActivity?.data || []}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis 
                          dataKey="hour" 
                          tickFormatter={formatHour}
                          tick={{ fontSize: 10 }}
                          stroke="#9ca3af"
                          interval={2}
                        />
                        <YAxis tick={{ fontSize: 12 }} stroke="#9ca3af" />
                        <Tooltip
                          labelFormatter={(hour) => formatHour(hour)}
                          contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb' }}
                        />
                        <Legend />
                        <Bar dataKey="views" name="Views" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="contacts" name="Contacts" fill="#10b981" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Top Performing Ads */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Award className="h-5 w-5 mr-2 text-yellow-500" />
                  Top Performing Ads
                </CardTitle>
                <CardDescription>Your best ads ranked by engagement</CardDescription>
              </CardHeader>
              <CardContent>
                {topAds.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-3 px-4 font-medium text-gray-500">Rank</th>
                          <th className="text-left py-3 px-4 font-medium text-gray-500">Listing</th>
                          <th className="text-center py-3 px-4 font-medium text-gray-500">Views</th>
                          <th className="text-center py-3 px-4 font-medium text-gray-500">Contacts</th>
                          <th className="text-center py-3 px-4 font-medium text-gray-500">Favorites</th>
                          <th className="text-center py-3 px-4 font-medium text-gray-500">Conversion</th>
                          <th className="text-center py-3 px-4 font-medium text-gray-500">Score</th>
                          <th className="text-center py-3 px-4 font-medium text-gray-500">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {topAds.map((ad, index) => (
                          <tr key={ad.listing_id} className="border-b hover:bg-gray-50">
                            <td className="py-3 px-4">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold ${
                                index === 0 ? 'bg-yellow-500' :
                                index === 1 ? 'bg-gray-400' :
                                index === 2 ? 'bg-orange-400' : 'bg-blue-400'
                              }`}>
                                {index + 1}
                              </div>
                            </td>
                            <td className="py-3 px-4">
                              <div className="flex items-center space-x-3">
                                {ad.image && (
                                  <img
                                    src={`${API_URL}${ad.image}`}
                                    alt={ad.title}
                                    className="w-12 h-12 object-cover rounded"
                                    onError={(e) => { e.target.style.display = 'none'; }}
                                  />
                                )}
                                <div>
                                  <p className="font-medium truncate max-w-[200px]">{ad.title}</p>
                                  <p className="text-sm text-gray-500">
                                    {formatCurrency(ad.price)}
                                  </p>
                                </div>
                              </div>
                            </td>
                            <td className="py-3 px-4 text-center">
                              <div className="flex items-center justify-center">
                                <Eye className="h-4 w-4 mr-1 text-blue-500" />
                                {ad.views}
                              </div>
                            </td>
                            <td className="py-3 px-4 text-center">
                              <div className="flex items-center justify-center">
                                <MousePointerClick className="h-4 w-4 mr-1 text-green-500" />
                                {ad.contacts}
                              </div>
                            </td>
                            <td className="py-3 px-4 text-center">
                              <div className="flex items-center justify-center">
                                <Heart className="h-4 w-4 mr-1 text-red-500" />
                                {ad.favorites}
                              </div>
                            </td>
                            <td className="py-3 px-4 text-center">
                              <Badge variant={ad.conversion_rate > 5 ? 'default' : 'secondary'}>
                                {ad.conversion_rate}%
                              </Badge>
                            </td>
                            <td className="py-3 px-4 text-center">
                              <span className="font-bold text-purple-600">{ad.engagement_score}</span>
                            </td>
                            <td className="py-3 px-4 text-center">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => navigate(`/listing/${ad.listing_id}`)}
                              >
                                <ExternalLink className="h-4 w-4" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <BarChart3 className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                    <p>No ad performance data yet</p>
                    <p className="text-sm mt-2">Post some ads to see analytics here</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
};

export default UserAnalytics;
